import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'hooks/store';

import { RootState } from 'store';
import { setUserData } from 'store/authorization';
import { ThunkGetUser } from 'store/user/thunks';
import { ThunkGetVerificationSettings } from 'store/verificationSettings/thunks';

import { REFRESH_TOKEN } from 'constants/localStorage';

import Loader from 'components/Loader';
import Sidebar from 'components/Sidebar';
import SoftphonePopupHost from 'components/CallScribe/SoftphonePopupHost';
import { isSoftphonePopupView } from 'components/CallScribe/popout';
import CallScribeWindowHost from 'components/CallScribe/CallScribeWindowHost';
import { isCallScribeWindowView } from 'components/CallScribe/callScribeWindow';
import { listenForSharedSignOut, listenForSignOut } from 'components/CallScribe/popupChannel';
import { usePopupLaunch } from 'components/CallScribe/usePopupLaunch';
import { getPodConfiguration } from 'components/Layout/podConfiguration';
import LaunchAnnouncer from 'components/CallScribe/LaunchAnnouncer';
import PopupSigningIn from 'components/CallScribe/PopupSigningIn';
import SigningIn from 'components/Layout/SigningIn';
import SignOutPending from 'components/Layout/SignOutPending';
import { isCallBusy, subscribeToCallIdle } from 'components/CallScribe/callBusy';
import { clearSession, sessionToken, sessionUserId } from 'utils/session';
import { dropPopupSession, holdPopupSession } from 'utils/popupSession';
import SlimNavigation from 'components/SlimNavigation';
import { useSlimDesign } from 'utils/useSlimDesign';

import useStyles from './styles';

import { PusherEvents } from 'constants/pusher';
import { SignalREvents } from 'constants/signalR';
import { PusherContext } from 'contexts/pusher';
import { useSignalREvent } from 'hooks/signalR';
import {
  AUTOTASK_POD,
  AUTOTASK_POD_WIDGET,
  FRESH_SERVICE_POD,
  FRESH_SERVICE_POD_WIDGET,
} from 'router/routes';
import { addClientPortalChat } from 'store/clientPortalChats';
import { setConfigurationData } from 'store/configuration';
import { ThunkGetMyPricePlan } from 'store/pricePlan/thunks';
import { addOnTop } from 'store/table';
import { UserDataSelector } from 'store/user';
import { SESSION_STORAGE_URL } from '../../constants/sessionStorage';

interface IProps {
  children: React.ReactNode;
  noSidebar?: boolean;
}

interface IPSATechVerification {
  TenantId: number;
  Id: number;
  From: string;
  To: string;
  CreatedDate: string;
  Code: string;
  ContactId: number;
  Type: string;
  CrmId: number;
  ContactName: string;
  IsVerified: boolean;
  TicketId: string;
}

interface IMetadata {
  Id: string;
  CrmId: string;
  Name: string;
  IsClosed: boolean;
  TicketId: string;
  TicketLink: string;
}

interface INewClientPortalChat {
  chatId: number;
  dateTime: string;
  message: string;
  metadata: string;
  type: 0;
  userName: 'TestContact TestContact';
}
const Layout: React.FC<IProps> = ({ children, noSidebar = false }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [showSidebar, setShowSidebar] = useState<boolean>(true);
  const isSlim = useSlimDesign();
  const classes = useStyles();

  const auxiliaryView = isSoftphonePopupView() || isCallScribeWindowView();

  // * LOCAL STORAGE ITEMS
  const accessToken = sessionToken();
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  const userId = sessionUserId();
  const { pusher } = useContext(PusherContext);

  const tokens = useAppSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const params = useParams();
  const searchParams = new URLSearchParams(location.search);
  const userData = useAppSelector(UserDataSelector);

  const launch = usePopupLaunch(auxiliaryView, holdPopupSession);
  const signingIn = launch.signingIn;

  const [signOutPending, setSignOutPending] = useState<boolean>(false);

  const endSession = auxiliaryView ? dropPopupSession : clearSession;

  const applySignOut = (): void => {
    endSession();
    setSignOutPending(false);
    navigate('/');
  };

  useEffect(() => {
    const onSignOut = (): void => {
      if (isCallBusy()) {
        setSignOutPending(true);
        return;
      }
      applySignOut();
    };
    const stopOpener = listenForSignOut(onSignOut);
    const stopShared = auxiliaryView ? listenForSharedSignOut(onSignOut) : () => undefined;
    return () => {
      stopOpener();
      stopShared();
    };
  }, []);

  useEffect(() => {
    if (!signOutPending) return undefined;
    const stop = subscribeToCallIdle(applySignOut);
    window.addEventListener('pagehide', endSession);
    return () => {
      stop();
      window.removeEventListener('pagehide', endSession);
    };
  }, [signOutPending]);

  // Getting user data and verification settings
  const getBaseData = async (userId: number): Promise<void> => {
    setLoading(true);
    await dispatch(ThunkGetUser(+userId));
    await dispatch(ThunkGetVerificationSettings());
    setLoading(false);
  };

  useEffect(() => {
    // Only a signed-in pod has a plan to fetch. Without this guard a pod opened with no session -
    // most commonly one embedded on a PSA ticket screen by someone who never signed in to it -
    // sent the request anyway, got a 401, and the axios interceptor answered with a full page
    // reload of /login. On production that was ~230k bounces a weekday, more /login page loads
    // than pod pages, each re-downloading the app and fetching the logo again. Signed out, the
    // effect below already routes to the login form without a reload.
    if (!userId || !accessToken) return;

    dispatch(ThunkGetMyPricePlan());
  }, [userId]);

  useEffect(() => {
    if (signingIn) return;
    if (!userId) {
      navigate('/');
    } else {
      getBaseData(+userId);
    }
  }, [userId, signingIn]);

  useLayoutEffect(() => {
    // Restoring Previous Session
    if (!tokens && accessToken && userId && !auxiliaryView) {
      dispatch(setUserData({ accessToken, refreshToken: refreshToken ?? '', userId }));
      return;
    }
    // Catching if tokens are not presented
    if (!tokens && !accessToken && !signingIn) {
      navigate('/');
    }
  }, [tokens, signingIn]);

  useEffect(() => {
    // getting params from url and storing it to configurations
    if (params?.psaId) {
      dispatch(setConfigurationData(getPodConfiguration(+params.psaId, searchParams)));
    }

    sessionStorage.setItem(SESSION_STORAGE_URL, `${location.pathname}${location.search}`);
  }, [location, searchParams]);

  useSignalREvent(SignalREvents.PsaTechVerification, data => {
    const res: IPSATechVerification = JSON.parse(data);
    dispatch(
      addOnTop({
        code: res.Code,
        contactCompanyId: null,
        contactCompanyName: null,
        contactId: res.ContactId,
        contactName: res.ContactName,
        createdDate: res.CreatedDate,
        crmId: res.CrmId,
        from: res.From,
        id: res.Id,
        isVerified: res.IsVerified,
        techAccountId: null,
        techAccountName: null,
        to: res.To,
        type: res.Type,
        verifiedDate: null,
        ticketId: res.TicketId,
      }),
    );
  });

  useEffect(() => {
    if (pusher && userData) {
      const channel = pusher.subscribe(`tenant-${userData.tenantId}`);

      channel.bind(PusherEvents.newClientPortalChat, (data: INewClientPortalChat) => {
        const metadata: IMetadata = JSON.parse(data.metadata);

        dispatch(
          addClientPortalChat({
            isClosed: false,
            name: metadata.Name,
            id: +metadata.Id,
            ticketId: metadata.TicketId,
            ticketNumber: undefined,
            ticketLink: metadata.TicketLink,
            contactName: '',
            assigneeName: '',
            isAnswered: false,
            lastMessage: '',
            lastMessageDate: '',
          }),
        );
      });
    }
  }, [pusher]);

  useLayoutEffect(() => {
    if (
      matchPath(AUTOTASK_POD, location.pathname) ??
      matchPath(AUTOTASK_POD_WIDGET, location.pathname) ??
      matchPath(FRESH_SERVICE_POD, location.pathname) ??
      matchPath(FRESH_SERVICE_POD_WIDGET, location.pathname)
    ) {
      setShowSidebar(false);
    }
  }, [location.pathname]);

  // Popup-mode short-circuit (BEFORE the `loading` gate). The softphone popup is on a
  // tight critical path for placing a call — it must not block on user-profile or
  // verification-settings fetches that the call view never reads. The URL-param effect
  // above has already populated `configuration.psaId` / `ticketId` synchronously from
  // the route, which is everything the popup actually needs.
  if (isSoftphonePopupView()) {
    return (
      <LaunchAnnouncer>
        {signingIn ? (
          <PopupSigningIn />
        ) : (
          <SoftphonePopupHost launchedByPod={launch.launchedByPod} signedInAs={launch.signedInAs} />
        )}
        {signOutPending && <SignOutPending />}
      </LaunchAnnouncer>
    );
  }

  if (signingIn) {
    return <SigningIn />;
  }

  if (isCallScribeWindowView()) {
    return (
      <>
        <CallScribeWindowHost />
        {signOutPending && <SignOutPending />}
      </>
    );
  }

  if (loading) return <Loader />;

  if (isSlim && showSidebar) {
    return (
      <div className={classes.slimLayout}>
        <SlimNavigation />
        <div className={classes.slimContent}>{children}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {showSidebar && <Sidebar />}
      {children}
    </div>
  );
};

export default Layout;
